import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { updateMe, linkedinCoach } from "@/lib/api";
import { Linkedin, Copy, CheckCircle2, Loader2, Sparkles, ChevronRight, ArrowRight, User } from "lucide-react";

const SECTIONS = [
  { id: "headline", label: "Headline", desc: "Your professional tagline (220 chars)" },
  { id: "about", label: "About", desc: "The summary section under your name" },
  { id: "experience", label: "Experience", desc: "A job description or bullet points" },
  { id: "skills", label: "Skills", desc: "Your listed skills section" },
  { id: "summary", label: "Summary", desc: "Overall profile summary" },
];

export default function LinkedInCoachPage() {
  const { user, refresh } = useAuth();
  const [section, setSection] = useState("headline");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  // LinkedIn URL flow (if not set)
  const [linkedinUrl, setLinkedinUrl] = useState(user?.linkedin_url || "");
  const [savingUrl, setSavingUrl] = useState(false);
  const [urlSaved, setUrlSaved] = useState(false);

  const hasLinkedIn = !!(user?.linkedin_url);
  const hasSkills = (user?.skills || []).length > 0;
  const active = SECTIONS.find(s => s.id === section);

  const handleSaveUrl = async (e) => {
    e.preventDefault();
    if (!linkedinUrl.trim()) return;
    setSavingUrl(true);
    try {
      await updateMe({ linkedin_url: linkedinUrl.trim() });
      await refresh();
      setUrlSaved(true);
    } catch (e) {
      setError("Could not save LinkedIn URL.");
    } finally {
      setSavingUrl(false);
    }
  };

  const handleOptimise = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      // Pass empty content — backend will use stored LinkedIn data automatically
      const { data } = await linkedinCoach(section, "");
      setResult(data);
    } catch (e) {
      setError("Optimisation failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const copyText = () => {
    if (result?.rewritten) {
      navigator.clipboard.writeText(result.rewritten);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // If no LinkedIn URL set yet, show URL capture first
  if (!hasLinkedIn && !urlSaved) {
    return (
      <div className="container-x py-10 md:py-14">
        <div className="mb-10">
          <span className="overline">LinkedIn Coach</span>
          <h1 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
            Connect your{" "}
            <span className="italic text-stone-500">LinkedIn first.</span>
          </h1>
          <p className="mt-3 text-stone-500 max-w-xl">
            We need your LinkedIn URL to analyse your profile and suggest improvements. Paste it below — we'll handle the rest.
          </p>
        </div>

        <div className="max-w-lg">
          <form onSubmit={handleSaveUrl} className="rounded-3xl border border-stone-200 bg-white p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center">
                <Linkedin size={22} className="text-[#0A66C2]" />
              </div>
              <div>
                <p className="font-display text-lg">Your LinkedIn profile URL</p>
                <p className="text-sm text-stone-500">e.g. linkedin.com/in/yourname</p>
              </div>
            </div>
            <input
              type="url"
              value={linkedinUrl}
              onChange={e => setLinkedinUrl(e.target.value)}
              placeholder="https://www.linkedin.com/in/yourhandle"
              className="w-full px-4 py-3 rounded-full border border-stone-200 bg-[#FDFBF7] text-stone-900 placeholder:text-stone-400 focus:outline-none focus:border-stone-900 text-sm mb-4"
            />
            <button type="submit" disabled={savingUrl || !linkedinUrl.trim()}
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-stone-900 text-stone-50 hover:bg-[#C2410C] transition-colors disabled:opacity-60 font-medium">
              {savingUrl ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
              {savingUrl ? "Saving…" : "Connect LinkedIn & start coaching"}
            </button>
            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="container-x py-10 md:py-14">
      <div className="mb-8">
        <span className="overline">LinkedIn Coach</span>
        <h1 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
          Turn your LinkedIn into{" "}
          <span className="italic text-stone-500">a recruiter magnet.</span>
        </h1>
      </div>

      {/* Connected profile banner */}
      <div className="mb-8 rounded-2xl bg-blue-50 border border-blue-100 p-5 flex items-center gap-4">
        <div className="w-10 h-10 rounded-xl bg-[#0A66C2] flex items-center justify-center shrink-0">
          <Linkedin size={18} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-stone-800 text-sm">
            LinkedIn connected · {hasSkills ? `${user.skills.length} skills detected` : "Profile data available"}
          </p>
          <p className="text-xs text-stone-500 truncate mt-0.5">{user?.linkedin_url}</p>
        </div>
        {(user?.headline) && (
          <p className="hidden md:block text-xs text-stone-500 italic truncate max-w-xs">"{user.headline}"</p>
        )}
        <CheckCircle2 size={18} className="text-emerald-500 shrink-0" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Section selector */}
        <div className="rounded-3xl border border-stone-200 bg-white p-8">
          <p className="overline mb-4">Choose section to optimise</p>
          <div className="space-y-2 mb-6">
            {SECTIONS.map(s => (
              <button key={s.id} onClick={() => { setSection(s.id); setResult(null); }}
                className={`w-full flex items-center justify-between px-4 py-3.5 rounded-2xl border text-left transition-colors ${
                  section === s.id
                    ? "border-stone-900 bg-stone-900 text-stone-50"
                    : "border-stone-100 hover:border-stone-300 hover:bg-stone-50"
                }`}>
                <div>
                  <p className="font-medium text-sm">{s.label}</p>
                  <p className={`text-xs mt-0.5 ${section === s.id ? "text-stone-400" : "text-stone-400"}`}>{s.desc}</p>
                </div>
                {section === s.id && <ChevronRight size={16} className="text-stone-400 shrink-0" />}
              </button>
            ))}
          </div>

          <div className="rounded-2xl bg-[#F5F3ED] border border-stone-200 p-4 mb-5">
            <p className="text-xs text-stone-500 font-mono uppercase tracking-widest mb-2">Using your data</p>
            <p className="text-sm text-stone-700">
              We'll use your fetched LinkedIn profile data automatically — no manual input needed. The AI has access to your headline, skills, and profile context.
            </p>
          </div>

          <button onClick={handleOptimise} disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-stone-900 text-stone-50 hover:bg-[#C2410C] transition-colors disabled:opacity-60 font-medium">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            {loading ? "Optimising…" : `Optimise my ${active?.label}`}
          </button>
          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        </div>

        {/* Result */}
        <div className="rounded-3xl border border-stone-200 bg-white p-8">
          {!result ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-16 text-stone-400 min-h-[300px]">
              <Linkedin size={40} className="mb-4 opacity-20" />
              <p className="max-w-xs text-sm">Select a section and click Optimise to get your AI-rewritten LinkedIn content.</p>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-5">
                <div>
                  <p className="overline">Optimised {result.section}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {result.current_score > 0 && (
                      <span className="text-xs text-stone-400 font-mono">Score before: {result.current_score}/100</span>
                    )}
                    {result.used_stored_data && (
                      <span className="text-xs text-blue-600 font-mono">· used LinkedIn data ✓</span>
                    )}
                  </div>
                </div>
                <button onClick={copyText}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-stone-900 text-stone-50 hover:bg-amber-300 hover:text-stone-900 transition-colors text-sm font-medium">
                  {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                  {copied ? "Copied!" : "Copy"}
                </button>
              </div>

              <div className="rounded-2xl bg-[#F5F3ED] border border-stone-200 p-5 mb-5">
                <p className="text-sm leading-relaxed text-stone-800 whitespace-pre-wrap">{result.rewritten}</p>
                <p className="text-xs text-stone-400 mt-2 text-right font-mono">{result.character_count} chars</p>
              </div>

              <div className="mb-5">
                <p className="overline mb-2">Why this is better</p>
                <p className="text-sm text-stone-700 leading-relaxed">{result.why}</p>
              </div>

              {result.quick_tips?.length > 0 && (
                <div>
                  <p className="overline mb-3">Quick tips</p>
                  <ul className="space-y-2">
                    {result.quick_tips.map((t, i) => (
                      <li key={i} className="flex gap-2 text-sm text-stone-700">
                        <span className="text-amber-500 shrink-0">•</span>{t}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
